import type { Model, ModelCatalogReview, ModelConfig, ModelProvider, ModelReviewField } from '../../types';
import { platformKeyState } from './ProviderRow';

/**
 * Derived figures for the Models page (task 1167): the summary strip, the
 * rail's per-provider counts, the Configs column, and the review's "differs"
 * marks. Pure functions over the four reads the page already makes.
 */

/** Providers whose Models the catalog review checks against a live provider list. */
export const LIVE_SOURCE_PROVIDER_KEYS = new Set(['anthropic', 'openai']);

export const hasPlatformKey = (provider: ModelProvider) => platformKeyState(provider).source !== 'none';

export function modelsByProvider(models: Model[]): Map<string, Model[]> {
  const map = new Map<string, Model[]>();
  for (const model of models) map.set(model.providerId, [...(map.get(model.providerId) ?? []), model]);
  return map;
}

export function configCountsByModel(configs: ModelConfig[]): Map<string, number> {
  const map = new Map<string, number>();
  for (const config of configs) map.set(config.modelId, (map.get(config.modelId) ?? 0) + 1);
  return map;
}

/** Model row id → the fields the review found out of date. */
export function differsByModel(review: ModelCatalogReview | undefined): Map<string, ModelReviewField[]> {
  const map = new Map<string, ModelReviewField[]>();
  for (const entry of review?.models ?? []) if (entry.differs.length > 0) map.set(entry.id, entry.differs);
  return map;
}

const PRICE_FIELDS = new Set<ModelReviewField>([
  'inputPriceMTok',
  'outputPriceMTok',
  'cacheReadPriceMTok',
  'cacheWritePriceMTok',
  'pricingTiers',
]);

/** Open differences in the latest review: every out-of-date value, split into prices and limits. */
export function differenceCounts(review: ModelCatalogReview | undefined): { total: number; prices: number; limits: number } {
  let prices = 0;
  let limits = 0;
  for (const entry of review?.models ?? []) {
    for (const field of entry.differs) {
      if (PRICE_FIELDS.has(field)) prices += 1;
      else limits += 1;
    }
  }
  return { total: prices + limits, prices, limits };
}

/** The newest "last checked" across the catalog, or null when nothing was ever checked. */
export function lastChecked(models: Model[]): string | null {
  let newest: string | null = null;
  for (const model of models) {
    if (model.lastSyncedAt && (!newest || Date.parse(model.lastSyncedAt) > Date.parse(newest))) newest = model.lastSyncedAt;
  }
  return newest;
}

export const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;
